<template>
  <div :class="['child-container', `child-${child.size}`]">
    <div class="child-container__header">
      <i :id="`move-${child.id}`" class="handler child-container__header-drag-icon drag-handler fas fa-grip-vertical" />

      <Tooltip :target="`move-${child.id}`" :text="$t('reposition')"></Tooltip>
      <Tooltip :target="`delete-button-${child.id}`" :text="$t('delete')"></Tooltip>

      <div class="child-container__header-title">{{ child.title }}</div>

      <div
        v-if="canRemoveBlock && isFormButton(child) && canItBeDeleted(child.title)"
        :id="`delete-button-${child.id}`"
        class="child-container__header-close"
        @click="removeItem(child.id)"
      >
        <i class="fal fa-times"></i>
      </div>
    </div>

    <div class="child-container__content">
      <Divider v-if="!isSmallBlock" padding-size="small" />

      <i
        class="handler resize-handler fas fa-arrows-alt-h"
        v-if="resizeable"
        v-resize="size => $emit('resize', { id: child.id, size })"
      />
      <div v-else class="child-container__left-border" />

      <div v-if="isSmallBlock" class="child-container__hidden-menu">
        <i class="far fa-ellipsis-h" />

        <ContentBlockActions
          :child-id="child.id"
          :vertical="true"
          :showDeleteButton="true"
          class="child-container__hidden-menu-actions"
          @delete="$emit('delete', $event)"
        />
      </div>
      <ContentBlockActions v-else :child-id="child.id" />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import Resize from '@/shared/directives/resize.js'
import Divider from '@/shared/components/Divider'
import ContentBlockActions from './ContentBlockActions'

export default {
  name: 'ContentBlock',
  props: {
    child: {
      type: Object,
      required: true,
    },
    resizeable: {
      type: Boolean,
      default: true,
    },
  },
  components: { Divider, ContentBlockActions },
  directives: { Resize },
  i18nOptions: { keyPrefix: 'components.sidenav.shared.content' },
  computed: {
    ...mapGetters('landing', ['active']),
    isSmallBlock() {
      return this.child.size <= 5
    },
    canRemoveBlock() {
      return !this.child.cantRemove && !this.isSmallBlock
    },
  },
  methods: {
    ...mapActions('landing', ['batchRemove', 'removeTabs']),
    isSectionOrPanel(id) {
      return id.startsWith('S-') || id.startsWith('P-')
    },
    isTabs(id) {
      return id.startsWith('T-')
    },
    isFormButton(child) {
      if (typeof child.item !== 'undefined') {
        if (typeof this.active.type !== 'undefined' && typeof child.item.type !== 'undefined') {
          return child.item.type !== 'Button' && this.active.type === 'widget/Form'
        }
      }
      return true
    },
    removeItem(id) {
      if (this.isSectionOrPanel(id)) {
        this.batchRemove({ id })
      } else if (this.isTabs(id)) {
        this.removeTabs({ id })
      } else {
        this.$emit('delete', id)
      }
    },
    canItBeDeleted(name) {
      return !(name === 'Nome Completo' || name === 'Email')
    },
  },
}
</script>

<style lang="scss" scoped>
.child-container {
  display: flex;
  flex-direction: column;
  background: white;
  margin: 5px;
  border-radius: 4px;
  padding: 8px 12px;
  position: relative;
  min-height: 90px;
  border: 1px solid $grey-20;

  .child-container__hidden-menu-actions {
    display: none;
  }

  @for $i from 1 through 12 {
    &.child-#{$i} {
      width: calc(((100% / 12) * #{$i}) - 10px);

      &[data-resizing='true'] {
        overflow: initial;
      }

      @if $i <= 5 {
        justify-content: space-between;

        .child-container__hidden-menu {
          &:hover {
            .child-container__hidden-menu-actions {
              z-index: 50;
              display: flex;
              background-color: white;
              padding: 8px;
              position: absolute;
              z-index: 100;
              left: 20px;
              top: 50px;
              border: 1px solid $cherry;
              border-radius: 3px;
            }
          }
        }
      }
    }
  }

  .handler,
  .handler {
    color: $cherry;
    cursor: grab;
    cursor: -webkit-grab;
    padding: 5px;
    &:active {
      cursor: -webkit-grabbing;
      cursor: grabbing;
    }

    &.resize-handler {
      position: absolute;
      right: 0;
      top: 0;
      height: 100%;
      padding: 0;
      width: 5px;
      background: $cherry;
      cursor: col-resize;
      border-radius: 0 5px 5px 0;
      &:before {
        content: unset;
      }

      &:active {
        cursor: col-resize;
      }

      &[data-show='true']:after {
        content: attr(data-width);
        position: absolute;
        top: -10px;
        left: -8px;
        background: red;
        border-radius: 50%;
        height: 20px;
        width: 20px;
        font-size: 12px;
        color: white;
        font-weight: bold;
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 2;
      }
    }
  }
}

.child-container__header {
  display: grid;
  grid-template-areas: 'drag-icon title close';
  grid-template-columns: 30px auto 30px;
}

.child-container__header-drag-icon {
  grid-area: drag-icon;
}

.child-container__header-title {
  grid-area: title;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  width: 80%;
}

.child-container__header-close {
  grid-area: close;
  text-align: center;
  cursor: pointer;
}

.child-container__left-border {
  color: $cherry;
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  padding: 0;
  width: 4px;
  background: $cherry;
  cursor: col-resize;
  border-radius: 5px 0 0 5px;
}

.child-container__content {
  padding-left: 16px;
  padding-right: 16px;
}
</style>
